'use client';

import { useEffect, useRef, useState } from 'react';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { InstallmentCalculatorDialog } from '@packages/installment-calculator/src/InstallmentCalculatorDialog/InstallmentCalculatorDialog.client';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';
import { useInstallmentCalculatorTracking } from '../../../Tracking/hooks/useInstallmentCalculatorTracking/useInstallmentCalculatorTracking';
import { SideBySideWithTipBadge } from '../SideBySideWithTipBadge/SideBySideWithTipBadge.client';
import { SideBySideWithIconBadge } from '../SideBySideWithIconBadge/SideBySideWithIconBadge.client';
import { IconInstallmentsAndLegalHints } from '../IconInstallmentsAndLegalHints/IconInstallmentsAndLegalHints.client';

export type InstallmentCalculatorInDialogProps = {
  /**
   * the single installment value from the calculation result as integer (e.g. 1337)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * the number of installments from the calculation result
   */
  numberOfInstallments: number;
  /**
   * Product price value as int (e.g. 1337)
   */
  basePrice: number;
  /**
   * the AB test ID for tracking
   * TODO -> remove when AB test is finished
   */
  abTestId: string;
};

export const InstallmentCalculatorInDialog = ({
  singleInstallment,
  currency,
  numberOfInstallments,
  basePrice,
  abTestId,
}: InstallmentCalculatorInDialogProps) => {
  const [dialogOpened, setDialogOpened] = useState(false);
  const {
    installmentCalculator: { InstallmentCalculatorCallToActionLayout },
  } = useConfig();

  const { trackGlycerinDisplayLink } = useInstallmentCalculatorTracking();

  const trackedOnce = useRef(false);

  // TODO -> remove when AB test is finished
  const { setOutcome } = useAbTesting();

  useEffect(() => {
    if (!trackedOnce.current) {
      trackGlycerinDisplayLink();
      // TODO -> remove when AB test is finished
      setOutcome(abTestId, { RATE_SEEN: 1 });
      trackedOnce.current = true;
    }
  }, [trackGlycerinDisplayLink, setOutcome, abTestId]);

  const handleOpenDialog = () => {
    // TODO -> remove when AB test is finished
    setOutcome(abTestId, { RATE_CLICKED: 1 });
    setDialogOpened(true);
  };

  return (
    <>
      <InstallmentCalculatorDialog
        basePrice={basePrice}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
      />
      {(() => {
        switch (InstallmentCalculatorCallToActionLayout?.layout) {
          case 'SideBySideWithTipBadge': {
            return (
              <SideBySideWithTipBadge
                singleInstallment={singleInstallment}
                currency={currency}
                numberOfInstallments={numberOfInstallments}
                openInstallmentCalculatorHandler={handleOpenDialog}
              />
            );
          }
          case 'SideBySideWithIconBadge': {
            return (
              <SideBySideWithIconBadge
                singleInstallment={singleInstallment}
                currency={currency}
                numberOfInstallments={numberOfInstallments}
                openInstallmentCalculatorHandler={handleOpenDialog}
              />
            );
          }
          case 'IconInstallmentsAndLegalHints': {
            return (
              <IconInstallmentsAndLegalHints
                singleInstallment={singleInstallment}
                currency={currency}
                openInstallmentCalculatorHandler={handleOpenDialog}
              />
            );
          }
          default:
            return null;
        }
      })()}
    </>
  );
};
